/**
 * Utility functions for managing favorites with localStorage
 */

const FAVORITES_KEY = "rolleston_homes_favorites";

export interface FavoritesState {
  favoriteIds: string[];
  lastUpdated: number;
}

/**
 * Get favorites from localStorage
 */
export const getFavoritesFromStorage = (): string[] => {
  try {
    const stored = localStorage.getItem(FAVORITES_KEY);
    if (!stored) return [];

    const data: FavoritesState = JSON.parse(stored);
    return data.favoriteIds || [];
  } catch (error) {
    console.warn("Failed to load favorites from localStorage:", error);
    return [];
  }
};

/**
 * Save favorites to localStorage
 */
export const saveFavoritesToStorage = (favoriteIds: string[]): void => {
  try {
    const data: FavoritesState = {
      favoriteIds,
      lastUpdated: Date.now(),
    };
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(data));
  } catch (error) {
    console.warn("Failed to save favorites to localStorage:", error);
  }
};

/**
 * Add a home to favorites
 */
export const addToFavorites = (homeId: string): string[] => {
  const currentFavorites = getFavoritesFromStorage();
  if (currentFavorites.includes(homeId)) {
    return currentFavorites; // Already favorited
  }

  const newFavorites = [...currentFavorites, homeId];
  saveFavoritesToStorage(newFavorites);
  return newFavorites;
};

/**
 * Remove a home from favorites
 */
export const removeFromFavorites = (homeId: string): string[] => {
  const currentFavorites = getFavoritesFromStorage();
  const newFavorites = currentFavorites.filter((id) => id !== homeId);
  saveFavoritesToStorage(newFavorites);
  return newFavorites;
};

/**
 * Toggle favorite status of a home
 */
export const toggleFavorite = (
  homeId: string
): { favoriteIds: string[]; isFavorite: boolean } => {
  const currentFavorites = getFavoritesFromStorage();
  const isFavorite = currentFavorites.includes(homeId);

  let newFavorites: string[];
  if (isFavorite) {
    newFavorites = removeFromFavorites(homeId);
  } else {
    newFavorites = addToFavorites(homeId);
  }

  return {
    favoriteIds: newFavorites,
    isFavorite: !isFavorite,
  };
};

/**
 * Check if a home is favorited
 */
export const isFavorited = (homeId: string): boolean => {
  const favorites = getFavoritesFromStorage();
  return favorites.includes(homeId);
};

/**
 * Clear all favorites
 */
export const clearFavorites = (): void => {
  try {
    localStorage.removeItem(FAVORITES_KEY);
  } catch (error) {
    console.warn("Failed to clear favorites from localStorage:", error);
  }
};

/**
 * Get favorites count
 */
export const getFavoritesCount = (): number => {
  return getFavoritesFromStorage().length;
};
